import { movementReasonService, movementService, tenantSettingsService } from 'services/api';
import { GridColDef, GridSelectionModel } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import { ConsumptionsColumns } from 'components/common/enhanced/common-headers/consumptions';
import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import * as React from 'react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker, DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import Box from '@mui/material/Box';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import { Button, ButtonGroup } from '@mui/material';
import { productWarehouseService } from 'services/api/ProductWarehouseService';
import toast from 'features/toast';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { IdentityField } from 'components/form/IdentityField';
import { MultipleAutocompleteAdapter } from 'components/form/MultipleAutocompleteAdapter';
import { IOption } from 'interfaces';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { IsSuperAdmin } from 'components/helpers/IsSuperAdmin';
import { isDirector } from 'components/helpers/IsDirector';

interface DateRangeQueryParams {
  timestamp__gte: string;
  timestamp__lte: string;
}

function buildDefaultDateRange(): DateRange<Dayjs> {
  const year = dayjs().year();
  const month = dayjs().month();
  const day = dayjs().subtract(1, 'month').daysInMonth();
  return [dayjs(`${year - 1}-${month + 1}-01`), dayjs(`${year}-${month}-${day}`)];
}

export function ConsumptionDetail() {
  const { t } = useTranslation();
  const datagridRefresh = useRef(null);
  const rowsRef = useRef(null);
  const isDir = isDirector();

  const defaultRange = buildDefaultDateRange();

  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(defaultRange);
  const [appliedRange, setAppliedRange] = useState<DateRange<Dayjs>>(defaultRange);
  const [params, setParams] = useState<DateRangeQueryParams>({
    timestamp__gte: defaultRange[0]?.format('YYYY-MM-DD'),
    timestamp__lte: defaultRange[1]?.format('YYYY-MM-DD')
  });

  const [productWarehouses, setProductWarehouses] = useState<any[]>([]);
  const [movementReasons, setMovementReasons] = useState<IOption[]>([]);
  const [tenantSettings, setTenantSettings] = useState<any>(null);

  const applyDateRange = useCallback(
    (rangeToApply?: DateRange<Dayjs>) => {
      const resolved = rangeToApply ?? dateRange;
      const [start, end] = resolved;
      if (!start?.isValid() || !end?.isValid()) {
        toast.error('Inserire date di inizio e fine valide.');
        return;
      }
      setAppliedRange([start, end]);
      setParams({
        timestamp__gte: start.format('YYYY-MM-DD'),
        timestamp__lte: end.format('YYYY-MM-DD')
      });
    },
    [dateRange]
  );

  const onChange = (date: DateRange<Dayjs>) => {
    setDateRange([date[0], date[1]]);
  };

  const onAccept = (date: DateRange<Dayjs>) => {
    setDateRange(date);
    applyDateRange(date);
  };

  useEffect(() => {
    tenantSettingsService.get(1).then(setTenantSettings);
    movementReasonService.getAllPaginated().then((res) => {
      setMovementReasons(res.results);
    });
  }, []);

  const columns: GridColDef[] = ConsumptionsColumns(appliedRange[0], appliedRange[1]);

  const onSelectionModelChange = (selectionModel: GridSelectionModel) => {
    const selectedRows = rowsRef.current.filter((row) => selectionModel.includes(row.id));
    setProductWarehouses(selectedRows);
  };

  const onSubmit = (values) => {
    tenantSettingsService.update(1, values).then(() => {
      toast.success('Causali aggiornate con successo.');
    });
  };

  const updateThresholds = () => {
    const newThresholds = productWarehouses.map((row) => {
      return {
        product: row.product,
        warehouse: row.warehouse,
        understock_threshold: Math.round(Math.abs(row.avg)) || 1,
        restock_threshold: Math.round(Math.abs(row.avg) / 2 + Math.abs(row.avg)) || 1,
        overstock_threshold: Math.round(Math.abs(row.avg) * 3) || 1
      };
    });
    productWarehouseService
      .updateThresholds(newThresholds)
      .then(() => toast.success(`Soglie aggiornate con successo`));
  };

  return (
    <TenantPage
      title={t('pages.consumption.title')}
      subtitle={t('pages.consumption.subtitle')}
      menuRight={
        <div className="flex items-center">
          <div className="mr-5 flex items-center">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateRangePicker
                inputFormat="DD/MM/YYYY"
                calendars={2}
                value={dateRange}
                onChange={onChange}
                onAccept={onAccept}
                closeOnSelect={false}
                componentsProps={{
                  actionBar: {
                    actions: ['cancel', 'accept']
                  }
                }}
                renderInput={(startProps, endProps) => (
                  <React.Fragment>
                    <TextField {...startProps} variant="standard" label={t('global.start')} />

                    <Box sx={{ mx: 2 }}>
                      <ArrowForwardIosIcon color={dateRange[1] ? 'primary' : 'disabled'} />
                    </Box>

                    <TextField {...endProps} variant="standard" label={t('global.end')} />
                  </React.Fragment>
                )}
              />
            </LocalizationProvider>
            <Button
              size="small"
              variant="outlined"
              className="ml-2"
              onClick={() => applyDateRange()}
              startIcon={<SearchIcon />}>
              {t('global.search')}
            </Button>
          </div>
          <IsTenantAdministrator>
            <ButtonGroup size="small">
              <Button onClick={updateThresholds} disabled={productWarehouses.length === 0}>
                <AutoFixHighIcon />
                &nbsp;
                {t(`actions.update-thresholds`)}
              </Button>
            </ButtonGroup>
          </IsTenantAdministrator>
        </div>
      }>
      {tenantSettings && !isDir && (
        <ReactFinalForm onSubmit={onSubmit}>
          <div className="flex flex-wrap">
            <IsSuperAdmin>
              <div className="p-2 w-full">
                <IdentityField
                  name="consumption_analysis_reasons"
                  component={MultipleAutocompleteAdapter}
                  options={movementReasons}
                  initValue={tenantSettings.consumption_analysis_reasons}
                  label={t('entities.tenant-settings.consumption-analysis-reasons')}
                  type="text"
                />
              </div>
            </IsSuperAdmin>
          </div>
        </ReactFinalForm>
      )}
      <div>
        <EnhancedDataGrid
          initialOptions={{
            pinnedColumns: {
              left: ['product', 'actions']
            }
          }}
          outerParams={params}
          service={movementService}
          refresh={datagridRefresh}
          getMethod={movementService.consumptionDetail}
          columns={columns}
          rows={rowsRef}
          checkboxSelection={true}
          selectionModelChange={onSelectionModelChange}
        />
      </div>
    </TenantPage>
  );
}

export default ConsumptionDetail;
